import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { articlesPageData, lineUrl } from "@/mocks/siteData";
import SEO from "@/components/base/SEO";

export default function ArticleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const article = articlesPageData.articles.find((a) => a.id === id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [id]);

  if (!article) {
    return (
      <>
        <SEO
          title="文章不存在｜時光逆行駭客"
          description="找不到您要的文章，請返回專欄文章列表瀏覽其他內容。"
          canonicalPath={`/articles/${id}`}
          noindex
        />
      <div className="min-h-screen bg-abyss-900 circuit-bg flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-ghost font-bold text-2xl mb-4">文章不存在</h2>
          <Link to="/articles" className="btn-gold-outline">
            返回專欄文章
          </Link>
        </div>
      </div>
    </>
    );
  }

  const currentIndex = articlesPageData.articles.findIndex((a) => a.id === id);
  const prevArticle = currentIndex > 0 ? articlesPageData.articles[currentIndex - 1] : null;
  const nextArticle = currentIndex < articlesPageData.articles.length - 1 ? articlesPageData.articles[currentIndex + 1] : null;

  // Related articles: same category, excluding current
  const relatedArticles = articlesPageData.articles
    .filter((a) => a.id !== id && a.category === article.category)
    .slice(0, 3);

  return (
    <>
      <SEO
        title={`${article.title}｜時光逆行駭客專欄`}
        description={article.excerpt}
        keywords={`${article.categoryLabel},逆齡知識,曾驛翔,時光逆行駭客,醫學美容`}
        canonicalPath={`/articles/${id}`}
        ogType="article"
        publishedTime={article.date}
        author="曾驛翔院長"
        schema={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": article.title,
          "description": article.excerpt,
          "author": {
            "@type": "Person",
            "name": "曾驛翔",
            "jobTitle": "院長",
          },
          "datePublished": article.date,
          "publisher": {
            "@type": "Organization",
            "name": "時光逆行駭客",
          },
        }}
      />
    <div className="min-h-screen bg-abyss-900 circuit-bg">
      {/* Hero Banner */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={`https://readdy.ai/api/search-image?query=elegant%20dark%20abstract%20medical%20aesthetic%20concept%20background%20with%20subtle%20flowing%20light%20patterns%20in%20gold%20and%20cyan%20color%20palette%20on%20deep%20navy%20black%20background%2C%20futuristic%20health%20technology%20atmosphere%2C%20minimalist%20composition%20with%20soft%20glow%2C%20no%20text%20no%20human&width=1920&height=500&seq=article-hero-${article.id}&orientation=landscape`}
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-abyss-900/80" />
          <div className="absolute inset-0 bg-gradient-to-b from-abyss-900/50 via-abyss-900/70 to-abyss-900" />
        </div>

        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="reveal">
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 text-ghost-muted text-base hover:text-cyber-gold transition-colors mb-6"
            >
              <i className="ri-arrow-left-line" />
              返回專欄文章
            </Link>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span
                className="px-2.5 py-1 rounded-md text-base font-mono font-medium"
                style={{
                  color: article.accent,
                  backgroundColor: `${article.accent}15`,
                }}
              >
                {article.categoryLabel}
              </span>
              <span className="text-ghost-dim text-base font-mono">{article.date}</span>
            </div>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-ghost leading-tight max-w-4xl">
              {article.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-8 md:py-16">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 800 }}>
          <div className="card-cyber p-8 md:p-12 reveal">
            <div className="space-y-6">
              {article.content?.map((paragraph, idx) => (
                <p
                  key={idx}
                  className="text-ghost-muted text-base md:text-lg leading-[1.8]"
                >
                  {paragraph}
                </p>
              ))}
              {!article.content && (
                <p className="text-ghost-muted text-base leading-relaxed">
                  {article.excerpt}
                </p>
              )}
            </div>

            {/* Author & Disclaimer */}
            <div className="mt-12 pt-8 border-t border-abyss-700">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-full bg-cyber-gold/10 border border-cyber-gold/20 flex items-center justify-center">
                  <i className="ri-user-line text-cyber-gold text-xl" />
                </div>
                <div>
                  <p className="text-ghost font-medium text-base">曾驛翔 院長</p>
                  <p className="text-ghost-dim text-base">時光逆行駭客｜皮膚科醫師</p>
                </div>
              </div>
              <p className="text-ghost-dim text-base leading-relaxed border-l-2 border-cyber-gold/20 pl-4">
                本內容為衛教觀念分享，不代表療效保證。實際狀況需由醫師於診間依個人條件專業評估。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Prev / Next Navigation */}
      <section className="py-8 md:py-12">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 800 }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {prevArticle ? (
              <button
                onClick={() => navigate(`/articles/${prevArticle.id}`)}
                className="card-cyber p-5 md:p-6 text-left hover:border-cyber-gold/40 transition-all duration-300 cursor-pointer group"
              >
                <span className="flex items-center gap-1.5 text-ghost-dim text-base mb-2 group-hover:text-cyber-gold transition-colors">
                  <i className="ri-arrow-left-line" />
                  上一篇
                </span>
                <p className="text-ghost font-medium text-base line-clamp-2">{prevArticle.title}</p>
              </button>
            ) : (
              <div />
            )}
            {nextArticle ? (
              <button
                onClick={() => navigate(`/articles/${nextArticle.id}`)}
                className="card-cyber p-5 md:p-6 text-right hover:border-cyber-gold/40 transition-all duration-300 cursor-pointer group"
              >
                <span className="flex items-center justify-end gap-1.5 text-ghost-dim text-base mb-2 group-hover:text-cyber-gold transition-colors">
                  下一篇
                  <i className="ri-arrow-right-line" />
                </span>
                <p className="text-ghost font-medium text-base line-clamp-2">{nextArticle.title}</p>
              </button>
            ) : (
              <div />
            )}
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-8 md:py-12 border-t border-abyss-800">
          <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 800 }}>
            <p className="font-mono text-base mb-6" style={{ color: article.accent }}>
              // RELATED ARTICLES
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedArticles.map((a) => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/articles/${a.id}`)}
                  className="card-cyber p-5 text-left hover:border-cyber-gold/40 transition-all duration-300 cursor-pointer group"
                >
                  <span
                    className="inline-block px-2 py-0.5 rounded text-sm font-mono mb-3"
                    style={{
                      color: a.accent,
                      backgroundColor: `${a.accent}15`,
                    }}
                  >
                    {a.categoryLabel}
                  </span>
                  <p className="text-ghost font-medium text-base line-clamp-2 group-hover:text-cyber-gold transition-colors">
                    {a.title}
                  </p>
                  <p className="text-ghost-dim text-sm mt-2">{a.date}</p>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-16 md:py-24 border-t border-abyss-800">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center reveal">
            <span className="section-label">// INITIATE BOOKING SEQUENCE</span>
            <h2 className="mt-4 text-2xl md:text-4xl font-bold text-ghost">
              讀完觀念，想親自評估自己的狀態？
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={lineUrl} target="_blank" rel="noopener noreferrer" className="btn-line whitespace-nowrap">
                <i className="ri-line-fill" />
                加入 LINE 諮詢
              </a>
              <Link to="/booking" className="btn-gold whitespace-nowrap">
                預約逆齡評估
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
}